import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { createOAuthClient, CreateOAuthClientResponse } from '../lib/api';

type Step = 1 | 2 | 3;

export function WelcomePage() {
  const navigate = useNavigate();
  const { user, session } = useAuth();
  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [client, setClient] = useState<CreateOAuthClientResponse | null>(null);
  const [selectedTool, setSelectedTool] = useState<'claude-code' | 'claude-desktop' | 'vscode-copilot' | 'github-copilot'>('claude-code');

  const getMcpEndpoint = () => {
    return 'https://mcp-server-staging.dalon-mckay.workers.dev/mcp';
  };

  const getDashboardBaseUrl = () => {
    return 'https://gxp-sentinel-dashboard.pages.dev';
  };

  useEffect(() => {
    if (step === 1 && !client && !loading) {
      createClient();
    }
  }, [step]);

  const createClient = async () => {
    if (!session) return;
    setLoading(true);
    setError(null);

    try {
      const redirectUri = `${window.location.origin}/auth/callback`;
      const res = await createOAuthClient(
        session.access_token,
        'My GxP Sentinel Client',
        [redirectUri]
      );

      if (!res.ok) {
        const errorData = await res.json() as { message?: string };
        throw new Error(errorData.message || 'Failed to create OAuth client');
      }

      const data = await res.json() as CreateOAuthClientResponse;
      setClient(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/', { replace: true });
  };

  const handleNext = () => {
    if (step < 3) {
      setStep((step + 1) as Step);
    } else {
      navigate('/', { replace: true });
    }
  };

  if (!user) return null;

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Setting up your credentials</h2>
        <p className="text-gray-600">We're creating your OAuth credentials so you can connect GxP Sentinel to your AI tools.</p>
      </div>

      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sentinel-700 mb-4"></div>
          <p className="text-gray-600">Creating your credentials...</p>
        </div>
      )}

      {error && (
        <div className="p-4 bg-red-50 text-red-700 rounded-md">
          <p className="font-medium">Something went wrong</p>
          <p className="text-sm mt-1">{error}</p>
          <button
            onClick={createClient}
            className="mt-3 text-sm text-red-700 hover:underline font-medium"
          >
            Try again
          </button>
        </div>
      )}

      {client && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-md">
          <p className="text-green-800 font-medium">✓ Credentials created successfully!</p>
          <p className="text-sm text-green-700 mt-1">Ready to connect to your AI tools.</p>
        </div>
      )}

      {client && (
        <button
          onClick={handleNext}
          className="w-full py-2 px-4 bg-sentinel-700 text-white rounded-md hover:bg-sentinel-900"
        >
          Continue to Next Step
        </button>
      )}
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Save your credentials</h2>
        <p className="text-gray-600 mb-4">Keep these safe — you'll need them to connect to your AI tools.</p>
      </div>

      {client && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Client ID</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={client.client_id}
                readOnly
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-50 font-mono text-sm"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(client.client_id);
                  alert('Copied!');
                }}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Copy
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-amber-900 mb-2 bg-amber-50 p-3 rounded-md border border-amber-200">
              ⚠️ Registration Token (Save Now — Won't Be Shown Again)
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={client.registration_access_token}
                readOnly
                className="flex-1 px-3 py-2 border border-amber-300 rounded-md bg-amber-50 font-mono text-sm"
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(client.registration_access_token);
                  alert('Copied!');
                }}
                className="px-4 py-2 border border-amber-300 bg-amber-50 text-amber-900 rounded-md hover:bg-amber-100"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-amber-700 mt-2">This token is displayed once only. Save it securely if you need it for advanced configurations.</p>
          </div>
        </div>
      )}

      <button
        onClick={handleNext}
        className="w-full py-2 px-4 bg-sentinel-700 text-white rounded-md hover:bg-sentinel-900"
      >
        I've Saved My Credentials
      </button>
    </div>
  );

  const getConfigSnippet = (tool: string): string => {
    const clientId = client?.client_id || '<YOUR_CLIENT_ID>';
    const dashboardUrl = getDashboardBaseUrl();
    const mcpUrl = getMcpEndpoint();

    switch (tool) {
      case 'claude-code':
        return `{
  "mcpServers": {
    "gxp-sentinel": {
      "type": "http",
      "url": "${mcpUrl}",
      "oauth": {
        "client_id": "${clientId}",
        "authorization_endpoint": "${dashboardUrl}/oauth/authorize",
        "token_endpoint": "${dashboardUrl}/api/oauth/token"
      }
    }
  }
}`;

      case 'claude-desktop':
        return `{
  "mcpServers": {
    "gxp-sentinel": {
      "url": "${mcpUrl}",
      "oauth": {
        "client_id": "${clientId}"
      }
    }
  }
}`;

      case 'vscode-copilot':
        return `{
  "mcp_servers": {
    "gxp-sentinel": {
      "url": "${mcpUrl}",
      "oauth": {
        "client_id": "${clientId}"
      }
    }
  }
}`;

      case 'github-copilot':
        return `{
  "mcp_servers": {
    "gxp-sentinel": {
      "url": "${mcpUrl}",
      "oauth": {
        "client_id": "${clientId}"
      }
    }
  }
}`;

      default:
        return '';
    }
  };

  const toolConfigs: Record<string, { label: string; description: string; file: string }> = {
    'claude-code': {
      label: 'Claude Code',
      description: 'Use GxP Sentinel with Claude Code (VS Code extension)',
      file: '~/.claude/settings.json (mcpServers)',
    },
    'claude-desktop': {
      label: 'Claude Desktop',
      description: 'Use GxP Sentinel with Claude Desktop application',
      file: '~/Library/Application Support/Claude/claude_desktop_config.json',
    },
    'vscode-copilot': {
      label: 'VS Code Copilot',
      description: 'Use GxP Sentinel with GitHub Copilot in VS Code',
      file: '.vscode/settings.json',
    },
    'github-copilot': {
      label: 'GitHub Copilot',
      description: 'Use GxP Sentinel with GitHub Copilot Chat',
      file: 'GitHub Copilot settings',
    },
  };

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold mb-2">Connect your AI tool</h2>
        <p className="text-gray-600">Copy the config for your tool and paste it into the right place.</p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {(Object.entries(toolConfigs) as Array<[string, typeof toolConfigs['claude-code']]>).map(([key, config]) => (
          <button
            key={key}
            onClick={() => setSelectedTool(key as typeof selectedTool)}
            className={`p-3 rounded-md border-2 text-left transition ${
              selectedTool === key
                ? 'border-sentinel-700 bg-sentinel-50'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <p className="font-medium text-sm">{config.label}</p>
            <p className="text-xs text-gray-600 mt-1">{config.description}</p>
          </button>
        ))}
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          Config for: <span className="text-sentinel-700">{toolConfigs[selectedTool]?.label}</span>
        </label>
        <p className="text-xs text-gray-600">
          File location: <code className="bg-gray-100 px-2 py-1 rounded text-xs">{toolConfigs[selectedTool]?.file}</code>
        </p>
      </div>

      <div>
        <div className="flex justify-between items-center mb-2">
          <label className="block text-sm font-medium text-gray-700">Copy this config:</label>
          <button
            onClick={() => {
              navigator.clipboard.writeText(getConfigSnippet(selectedTool));
              alert('Config copied to clipboard!');
            }}
            className="text-sm text-sentinel-700 hover:underline font-medium"
          >
            Copy All
          </button>
        </div>
        <div className="bg-gray-900 text-gray-100 rounded-md p-4 font-mono text-xs overflow-x-auto">
          <pre>{getConfigSnippet(selectedTool)}</pre>
        </div>
      </div>

      <div className="border-t pt-4 space-y-2">
        <button
          onClick={handleNext}
          className="w-full py-2 px-4 bg-sentinel-700 text-white rounded-md hover:bg-sentinel-900"
        >
          Go to Dashboard
        </button>
        <button
          onClick={handleSkip}
          className="w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
        >
          Skip for Now
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center px-4">
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-lg shadow-lg p-8 md:p-12">
          <div className="mb-8">
            <div className="flex gap-2 justify-center">
              {[1, 2, 3].map((s) => (
                <div
                  key={s}
                  className={`h-2 flex-1 rounded-full transition ${
                    s <= step ? 'bg-sentinel-700' : 'bg-gray-200'
                  }`}
                />
              ))}
            </div>
            <p className="text-center text-sm text-gray-600 mt-2">
              Step {step} of 3
            </p>
          </div>

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
        </div>

        <div className="mt-6 text-center">
          <button
            onClick={handleSkip}
            className="text-gray-600 hover:text-gray-800 text-sm"
          >
            Skip setup and go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
